"use client";

import * as React from "react";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOwnTheme } from "@/contexts/theme-context";
import { useHistoryManager } from "@/hooks/use-history-manager";
import { useMobile } from "@/hooks/use-mobile";
import { composeRefs } from "@/utils/compose-refs";
import { cn } from "@/utils/lib";
import { getSameOriginLinkUrlFromEvent } from "@/utils/navigation-events";

type DropdownDrawerType = "drawer" | "dropdown";

type DropdownDrawerStyle = React.CSSProperties & {
  "--dropdown-drawer-section-radius"?: string;
};

type DropdownDrawerPanel = {
  content: React.ReactNode;
  title: string;
};

type DropdownDrawerContextValue = {
  activePanelId: string | null;
  close: () => void;
  dismiss: () => void;
  open: boolean;
  panels: Record<string, DropdownDrawerPanel>;
  popPanel: () => void;
  pushPanel: (panelId: string) => void;
  registerPanel: (panelId: string, panel: DropdownDrawerPanel) => void;
  resetPanels: () => void;
  title: string;
  type: DropdownDrawerType;
  unregisterPanel: (panelId: string) => void;
};

type DropdownDrawerSubContextValue = {
  panelId: string;
};

const DropdownDrawerContext =
  React.createContext<DropdownDrawerContextValue | null>(null);
const DropdownDrawerSubContext =
  React.createContext<DropdownDrawerSubContextValue | null>(null);

const useDropdownDrawerContext = () => {
  const context = React.useContext(DropdownDrawerContext);

  if (!context) {
    throw new Error(
      "useDropdownDrawerContext must be used within DropdownDrawer",
    );
  }

  return context;
};

const useDropdownDrawerSubContext = () => {
  const context = React.useContext(DropdownDrawerSubContext);

  if (!context) {
    throw new Error(
      "useDropdownDrawerSubContext must be used within DropdownDrawerSub",
    );
  }

  return context;
};

export type DropdownDrawerProps = {
  children: React.ReactNode;
  defaultOpen?: boolean;
  drawerProps?: React.ComponentProps<typeof Drawer>;
  dropdownProps?: React.ComponentProps<typeof DropdownMenu>;
  historyName?: string;
  historyReplace?: boolean;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  title: string;
};

export const DropdownDrawer = ({
  children,
  defaultOpen = false,
  drawerProps,
  dropdownProps,
  historyName,
  historyReplace = false,
  onOpenChange,
  open: openProp,
  title,
}: DropdownDrawerProps) => {
  const { isMobile } = useMobile();
  const [hasMounted, setHasMounted] = React.useState(false);
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const [panelStack, setPanelStack] = React.useState<string[]>([]);
  const [panels, setPanels] = React.useState<
    Record<string, DropdownDrawerPanel>
  >({});
  const panelStackRef = React.useRef(panelStack);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  panelStackRef.current = panelStack;

  React.useEffect(() => {
    setHasMounted(true);
  }, []);

  const isDrawer = hasMounted && isMobile;

  const setOpen = React.useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }

      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange],
  );

  const popPanel = React.useCallback(() => {
    setPanelStack((current) => current.slice(0, -1));
  }, []);

  const pushPanel = React.useCallback((panelId: string) => {
    setPanelStack((current) => [...current, panelId]);
  }, []);

  const resetPanels = React.useCallback(() => {
    setPanelStack([]);
  }, []);

  const registerPanel = React.useCallback(
    (panelId: string, panel: DropdownDrawerPanel) => {
      setPanels((current) => ({
        ...current,
        [panelId]: panel,
      }));
    },
    [],
  );

  const unregisterPanel = React.useCallback((panelId: string) => {
    setPanels((current) => {
      const nextPanels = { ...current };
      delete nextPanels[panelId];
      return nextPanels;
    });
  }, []);

  const history = useHistoryManager({
    enabled: isDrawer,
    kind: "dropdown-drawer",
    name: historyName ?? title,
    onBrowserBack: () => {
      if (panelStackRef.current.length > 0) {
        popPanel();
        return "handled";
      }

      return "close";
    },
    onOpenChange: setOpen,
    open,
    replace: historyReplace,
  });
  const historyOnOpenChange = history.onOpenChange;
  const dismissHistoryEntry = history.dismiss;

  const activePanelId = panelStack.at(-1) ?? null;
  const rootOnOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        resetPanels();
      }

      if (isDrawer) {
        historyOnOpenChange(nextOpen);
      } else {
        setOpen(nextOpen);
      }
    },
    [historyOnOpenChange, isDrawer, resetPanels, setOpen],
  );
  const close = React.useCallback(
    () => rootOnOpenChange(false),
    [rootOnOpenChange],
  );
  const dismiss = React.useCallback(() => {
    if (isDrawer) {
      dismissHistoryEntry();
      return;
    }

    setOpen(false);
  }, [dismissHistoryEntry, isDrawer, setOpen]);

  const context = React.useMemo<DropdownDrawerContextValue>(
    () => ({
      activePanelId,
      close,
      dismiss,
      open,
      panels,
      popPanel,
      pushPanel,
      registerPanel,
      resetPanels,
      title,
      type: isDrawer ? "drawer" : "dropdown",
      unregisterPanel,
    }),
    [
      activePanelId,
      close,
      dismiss,
      isDrawer,
      open,
      panels,
      popPanel,
      pushPanel,
      registerPanel,
      resetPanels,
      title,
      unregisterPanel,
    ],
  );

  const content = (
    <DropdownDrawerContext.Provider value={context}>
      {children}
    </DropdownDrawerContext.Provider>
  );

  if (isDrawer) {
    return (
      <Drawer open={open} onOpenChange={rootOnOpenChange} {...drawerProps}>
        {content}
      </Drawer>
    );
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={rootOnOpenChange}
      {...dropdownProps}
    >
      {content}
    </DropdownMenu>
  );
};

export const DropdownDrawerTrigger = ({
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuTrigger>) => {
  const { type } = useDropdownDrawerContext();

  return type === "drawer" ? (
    <DrawerTrigger {...(props as React.ComponentProps<typeof DrawerTrigger>)}>
      {children}
    </DrawerTrigger>
  ) : (
    <DropdownMenuTrigger {...props}>{children}</DropdownMenuTrigger>
  );
};

type DropdownDrawerContentProps = React.ComponentProps<
  typeof DropdownMenuContent
> & {
  drawerBodyClassName?: string;
  drawerClassName?: string;
};

export const DropdownDrawerContent = ({
  align,
  alignOffset,
  children,
  className,
  drawerBodyClassName,
  drawerClassName,
  onCloseAutoFocus,
  side,
  sideOffset,
  style,
  ...props
}: DropdownDrawerContentProps) => {
  const { activePanelId, open, panels, popPanel, resetPanels, title, type } =
    useDropdownDrawerContext();
  const { theme } = useOwnTheme();
  const sectionRadius = theme?.startsWith("paper") ? "4px" : "var(--radius)";
  const drawerStyle: DropdownDrawerStyle = {
    "--dropdown-drawer-section-radius": sectionRadius,
    ...style,
  };

  if (type === "dropdown") {
    return (
      <DropdownMenuContent
        align={align}
        alignOffset={alignOffset}
        className={className}
        onCloseAutoFocus={onCloseAutoFocus}
        side={side}
        sideOffset={sideOffset}
        style={style}
        {...props}
      >
        {children}
      </DropdownMenuContent>
    );
  }

  const activePanel = activePanelId ? panels[activePanelId] : null;

  return (
    <DrawerContent
      className={cn("border-border-light bg-background p-0", className)}
      bodyClassName={cn("flex flex-col overflow-hidden", drawerBodyClassName)}
      onTransitionEnd={(event) => {
        if (event.currentTarget === event.target && !open) {
          resetPanels();
        }
      }}
      style={drawerStyle}
    >
      <DrawerTitle className="sr-only">{title}</DrawerTitle>
      <DrawerDescription className="sr-only">
        Choose an action.
      </DrawerDescription>
      <div
        className={cn("flex min-h-0 flex-1 overflow-hidden", drawerClassName)}
      >
        <div
          className="flex min-h-0 flex-1 transition-transform duration-200 ease-out"
          style={{
            transform: activePanelId ? "translateX(-100%)" : "translateX(0)",
          }}
        >
          <div className={dropdownDrawerMenuClassName} role="menu">
            {children}
          </div>
          <div className={dropdownDrawerMenuClassName} role="menu">
            <div className={dropdownDrawerPanelHeaderClassName}>
              <button
                type="button"
                className="
                  unforce-radius flex w-full items-center gap-3 p-4 text-left
                  hover:bg-accent hover:text-muted-foreground
                "
                onClick={popPanel}
              >
                <LuChevronLeft className="shrink-0 text-xl text-muted" />
                <span className="truncate font-medium">
                  {activePanel?.title ?? "Back"}
                </span>
              </button>
            </div>
            {activePanel?.content}
          </div>
        </div>
      </div>
    </DrawerContent>
  );
};

const dropdownDrawerMenuClassName = `
  min-h-0 min-w-full shrink-0 overflow-y-auto overscroll-contain p-3
  [-webkit-overflow-scrolling:touch]
`;

const dropdownDrawerPanelHeaderClassName = `
  mb-3 overflow-hidden rounded-[var(--dropdown-drawer-section-radius)]
  border border-border-light bg-card
`;

const dropdownDrawerItemClassName = `
  unforce-radius flex w-full cursor-pointer items-center gap-3 rounded-none border-b
  border-border-light bg-card p-4 text-left text-sm text-foreground
  first:rounded-t-[var(--dropdown-drawer-section-radius)]
  last:rounded-b-[var(--dropdown-drawer-section-radius)] last:border-b-0
  [[role=separator]+&]:rounded-t-[var(--dropdown-drawer-section-radius)]
  [&:has(+_[role=separator])]:rounded-b-[var(--dropdown-drawer-section-radius)]
  [&:has(+_[role=separator])]:border-b-0
  hover:bg-accent hover:text-muted-foreground
  data-disabled:pointer-events-none data-disabled:opacity-50
`;

type DropdownDrawerItemProps = React.ComponentProps<typeof DropdownMenuItem>;

export const DropdownDrawerItem = ({
  ref,
  asChild,
  children,
  className,
  closeOnClick,
  closeOnSelect,
  disabled,
  inset,
  nativeButton,
  onClick,
  onSelect,
  render,
  ...props
}: DropdownDrawerItemProps & {
  ref?: React.Ref<HTMLElement>;
}) => {
  const { close, dismiss, type } = useDropdownDrawerContext();
  void nativeButton;

  if (type === "dropdown") {
    return (
      <DropdownMenuItem
        ref={ref}
        asChild={asChild}
        className={cn(inset && "pl-8", className)}
        closeOnClick={closeOnClick}
        closeOnSelect={closeOnSelect}
        disabled={disabled}
        onClick={onClick}
        onSelect={onSelect}
        render={render}
        {...props}
      >
        {children}
      </DropdownMenuItem>
    );
  }

  const shouldClose = closeOnSelect ?? closeOnClick ?? true;
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    onClick?.(event as Parameters<NonNullable<typeof onClick>>[0]);
    onSelect?.(event as Parameters<NonNullable<typeof onSelect>>[0]);

    if (event.defaultPrevented) {
      return;
    }

    if (getSameOriginLinkUrlFromEvent(event)) {
      dismiss();
      return;
    }

    if (shouldClose) {
      close();
    }
  };

  const renderChild =
    React.isValidElement(render) || (asChild && React.isValidElement(children))
      ? (render ?? React.Children.only(children))
      : null;

  if (React.isValidElement(renderChild)) {
    const child = renderChild as React.ReactElement<{
      "aria-disabled"?: boolean | "false" | "true";
      className?: string;
      onClick?: (event: React.MouseEvent<HTMLElement>) => void;
      ref?: React.Ref<HTMLElement>;
      role?: string;
    }>;
    const childProps = child.props;

    return React.cloneElement(child, {
      ...childProps,
      "aria-disabled": disabled || childProps["aria-disabled"],
      className: cn(
        childProps.className,
        dropdownDrawerItemClassName,
        inset && "pl-8",
        className,
      ),
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        childProps.onClick?.(event);

        if (!event.defaultPrevented) {
          handleClick(event);
        }
      },
      ref: composeRefs(ref, childProps.ref),
      role: childProps.role ?? "menuitem",
    } as React.HTMLAttributes<HTMLElement>);
  }

  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type="button"
      className={cn(dropdownDrawerItemClassName, inset && "pl-8", className)}
      disabled={disabled}
      onClick={handleClick}
      role="menuitem"
    >
      {children}
    </button>
  );
};

export const DropdownDrawerSeparator = (
  props: React.ComponentProps<typeof DropdownMenuSeparator>,
) => {
  const { type } = useDropdownDrawerContext();

  return type === "dropdown" ? (
    <DropdownMenuSeparator {...props} />
  ) : (
    <div role="separator" className={cn("h-3", props.className)} />
  );
};

type DropdownDrawerSubProps = Omit<
  React.ComponentProps<typeof DropdownMenuSub>,
  "children"
> & {
  children: React.ReactNode;
};

export const DropdownDrawerSub = ({
  children,
  ...props
}: DropdownDrawerSubProps) => {
  const { type } = useDropdownDrawerContext();
  const panelId = React.useId();

  return (
    <DropdownDrawerSubContext.Provider value={{ panelId }}>
      {type === "dropdown" ? (
        <DropdownMenuSub {...props}>{children}</DropdownMenuSub>
      ) : (
        children
      )}
    </DropdownDrawerSubContext.Provider>
  );
};

export const DropdownDrawerSubTrigger = ({
  children,
  className,
  closeDelay,
  delay,
  disabled,
  label,
  openOnHover,
  render,
  ...props
}: React.ComponentProps<typeof DropdownMenuSubTrigger> & {
  title?: string;
}) => {
  const { pushPanel, type } = useDropdownDrawerContext();
  const { panelId } = useDropdownDrawerSubContext();
  const handledTouchRef = React.useRef(false);
  void closeDelay;
  void delay;
  void label;
  void openOnHover;
  void render;

  const openPanel = React.useCallback(() => {
    if (!disabled) {
      pushPanel(panelId);
    }
  }, [disabled, panelId, pushPanel]);

  if (type === "dropdown") {
    return (
      <DropdownMenuSubTrigger className={className} {...props}>
        {children}
      </DropdownMenuSubTrigger>
    );
  }

  return (
    <button
      type="button"
      className={cn(dropdownDrawerItemClassName, "justify-between", className)}
      onClick={(event) => {
        if (event.defaultPrevented) {
          return;
        }

        if (handledTouchRef.current) {
          handledTouchRef.current = false;
          return;
        }

        openPanel();
      }}
      onTouchEnd={(event) => {
        if (event.defaultPrevented || disabled) {
          return;
        }

        // After the panel slides in, mobile's compatibility click can land on
        // an incoming submenu item and immediately close the drawer.
        event.preventDefault();
        handledTouchRef.current = true;
        openPanel();

        window.setTimeout(() => {
          handledTouchRef.current = false;
        }, 500);
      }}
      role="menuitem"
      disabled={disabled}
    >
      <span className="flex min-w-0 items-center gap-3 truncate">
        {children}
      </span>
      <LuChevronRight className="ml-auto shrink-0 text-xl text-muted" />
    </button>
  );
};

export const DropdownDrawerSubContent = ({
  children,
  title,
  ...props
}: React.ComponentProps<typeof DropdownMenuSubContent> & {
  title: string;
}) => {
  const { registerPanel, type, unregisterPanel } = useDropdownDrawerContext();
  const { panelId } = useDropdownDrawerSubContext();

  React.useEffect(() => {
    if (type !== "drawer") {
      return;
    }

    registerPanel(panelId, {
      content: children,
      title,
    });

    return () => {
      unregisterPanel(panelId);
    };
  }, [children, panelId, registerPanel, title, type, unregisterPanel]);

  if (type === "dropdown") {
    return (
      <DropdownMenuSubContent {...props}>{children}</DropdownMenuSubContent>
    );
  }

  return null;
};
